import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { inputRecipe, selectTriggerRecipe } from '../recipes/input.recipe';
import {
  optionSheetItemRecipe,
  optionSheetSearchInputRecipe,
  optionSheetSearchRecipe,
} from '../recipes/sheet.recipe';
import { useFieldControlProps } from './field-context';
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetTrigger,
} from './BottomSheet';
import type { SelectOption } from './Select';
import { Text } from './Text';

export interface OptionSheetProps {
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  /** Shown in the field until something is chosen. */
  placeholder?: string;
  /** The sheet's heading — usually the field's label: "Priority". */
  title: string;
  /**
   * A search box along the bottom, and the sheet at full height. For a list
   * long enough to scroll — vehicle makes, accounts — not for four
   * priorities, where a search box is one more thing to read past.
   */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Said when a search matches nothing. */
  emptyMessage?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * Select, for a phone: the field looks the same, but the options rise in a
 * bottom sheet instead of dropping from the field.
 *
 * A popover list under a field is sized for a pointer. On a phone it opens
 * wherever the field happens to be, often half off the screen, with 14px rows
 * under a thumb. The sheet puts the list where the thumb is and makes the rows
 * big enough to hit.
 *
 * Takes the same props as Select's `options` form, so moving a field across
 * is a rename — and inside a FormField it is wired the same way:
 *
 *   <FormField form={form} name="priority" label="Priority">
 *     {(field) => (
 *       <OptionSheet
 *         title="Priority"
 *         options={PRIORITIES}
 *         value={field.value}
 *         onValueChange={field.onChange}
 *       />
 *     )}
 *   </FormField>
 *
 * Opened from inside a BottomSheet it stacks over it, like any other sheet.
 */
export function OptionSheet({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  title,
  searchable = false,
  searchPlaceholder = 'Search',
  emptyMessage = 'Nothing matches that.',
  id,
  disabled,
  required,
  className,
}: OptionSheetProps) {
  const fieldProps = useFieldControlProps({ id, disabled, required });
  const [open, setOpen] = React.useState(false);

  const selected = options.find((option) => option.value === value);

  const choose = (next: string) => {
    onValueChange?.(next);
    setOpen(false);
  };

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <BottomSheetTrigger asChild>
        <button
          type="button"
          id={fieldProps.id}
          disabled={fieldProps.disabled}
          aria-describedby={fieldProps['aria-describedby']}
          aria-invalid={fieldProps['aria-invalid']}
          aria-required={fieldProps.required || undefined}
          // Select's trigger reads this to mute the placeholder; the same
          // attribute keeps the two fields looking the same.
          data-placeholder={selected ? undefined : ''}
          className={cn(inputRecipe(), selectTriggerRecipe(), className)}
        >
          <span>{selected ? selected.label : placeholder}</span>
          <ChevronDown className="size-icon-sm shrink-0 opacity-60" />
        </button>
      </BottomSheetTrigger>

      <BottomSheetContent title={title} size={searchable ? 'full' : 'auto'}>
        {/* defaultValue, not value: it starts the highlight on the current
            choice, and a controlled value would pin it there against the
            arrow keys. */}
        <CommandPrimitive label={title} defaultValue={value} loop>
          <CommandPrimitive.List>
            <CommandPrimitive.Empty>
              <Text variant="body" tone="muted">
                {emptyMessage}
              </Text>
            </CommandPrimitive.Empty>
            {options.map((option) => (
              <CommandPrimitive.Item
                key={option.value}
                // cmdk filters on `value`, which here is an id; the label is
                // what someone searching for "Chevy" is actually typing at.
                value={option.value}
                keywords={
                  typeof option.label === 'string' ? [option.label] : undefined
                }
                disabled={option.disabled}
                onSelect={choose}
                className={optionSheetItemRecipe()}
              >
                {option.label}
                {option.value === value ? (
                  <>
                    <Check
                      aria-hidden="true"
                      className="size-icon-md shrink-0 text-muted-foreground"
                    />
                    {/* cmdk spends aria-selected on the highlight, so the
                        chosen option has to say so in words. */}
                    <span className="sr-only">(current)</span>
                  </>
                ) : null}
              </CommandPrimitive.Item>
            ))}
          </CommandPrimitive.List>

          {searchable ? (
            <div className={optionSheetSearchRecipe()}>
              <Search className="size-icon-sm shrink-0" aria-hidden="true" />
              <CommandPrimitive.Input
                placeholder={searchPlaceholder}
                className={optionSheetSearchInputRecipe()}
              />
            </div>
          ) : null}
        </CommandPrimitive>
      </BottomSheetContent>
    </BottomSheet>
  );
}
